// Home-screen promotion notice for clients: the promotions live now (and any starting soon), each
// with its headline offer and end date, linking to the full explanation on the Promotions page.
// Dismissable per promotion — it comes back for a promotion the buyer hasn't dismissed yet.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyPromotions } from '../api/me.js';
import { safeGet, safeSet } from '../lib/storage.js';
import { offerLine, timingLine } from '../lib/promotions.js';
import { DiscountIcon, CloseIcon } from './icons.jsx';

const DISMISSED_KEY = 'wholesale.dismissedPromos';
const readDismissed = () => {
  try {
    return JSON.parse(safeGet(DISMISSED_KEY) || '[]');
  } catch {
    return [];
  }
};

export default function PromotionsBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    fetchMyPromotions().then(setData).catch(() => setData(null));
  }, []);

  const shown = (data?.promotions || []).filter((p) => !dismissed.includes(p.id));
  if (!shown.length) return null;

  const dismiss = () => {
    const next = [...new Set([...dismissed, ...shown.map((p) => p.id)])];
    safeSet(DISMISSED_KEY, JSON.stringify(next));
    setDismissed(next);
  };
  const live = shown.some((p) => p.status === 'LIVE');

  return (
    <div role="status" className="relative mb-4 rounded-lg border border-accent/40 bg-accent-light p-4 pr-10 shadow-sm">
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="absolute right-3 top-3 text-text-muted hover:text-text-primary">
        <CloseIcon size={16} />
      </button>
      <div className="flex items-start gap-3">
        <DiscountIcon size={22} className="mt-0.5 shrink-0 text-accent" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-text-primary">
            {live ? (shown.length > 1 ? 'You have promotions available' : 'You have a promotion available') : 'A promotion is coming up for you'}
          </div>
          <ul className="mt-1.5 space-y-1.5">
            {shown.map((p) => (
              <li key={p.id} className="text-sm">
                <span className="font-medium text-text-primary">{p.name}</span>
                <span className="text-text-secondary"> — {offerLine(p, data.currency)}.</span>
                <span className="block text-xs text-text-muted">{timingLine(p)} · applied automatically, no code needed</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            {live && (
              <Link to="/catalog" className="rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover">
                Place an order
              </Link>
            )}
            <Link to="/promotions" className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised">
              See how it works
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
